import { useLayoutEffect, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/cn';

export type PopoverAlign = 'start' | 'end';

export interface PopoverProps {
  /** The control the panel is positioned against. */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  children: ReactNode;
  align?: PopoverAlign;
  /**
   * Never narrower than the anchor - what a select menu wants, a date picker
   * does not.
   *
   * A minimum rather than a fixed width. It was a fixed one, which is right
   * for a full-width field and wrong everywhere else: a narrow trigger made a
   * narrow menu, so a stage picker sitting in a table column offered
   * "Prop...", "Neg..." and a single "N." - every option truncated to the
   * width of whichever one happened to be selected.
   */
  matchAnchorWidth?: boolean;
  className?: string;
  id?: string;
  role?: string;
  'aria-label'?: string;
}

interface Position {
  top: number;
  left: number;
  width: number;
}

const GAP = 4;
const VIEWPORT_PADDING = 8;

/**
 * A panel anchored to a trigger, rendered in a portal on `document.body`.
 *
 * The portal is the point: rendered in place, a dropdown inside a table cell or
 * a drawer gets clipped by the first ancestor with `overflow: hidden`. Position
 * is therefore computed in viewport coordinates and the panel is `fixed`.
 *
 * It flips above the anchor when there is no room below, and shifts
 * horizontally to stay on screen.
 */
export function Popover({ open, ...props }: PopoverProps) {
  /* The panel is a separate component mounted only while open, so its measured
     position is discarded on close automatically - no reset to manage, and no
     chance of flashing at the previous anchor's coordinates when reopened. */
  if (!open) {
    return null;
  }

  return <PopoverPanel {...props} />;
}

function PopoverPanel({
  anchorRef,
  children,
  align = 'start',
  matchAnchorWidth = false,
  className,
  id,
  role,
  'aria-label': ariaLabel,
}: Omit<PopoverProps, 'open'>) {
  const [position, setPosition] = useState<Position | null>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);

  /**
   * Measured in a layout effect so the panel is placed before the browser
   * paints - otherwise it is visible for one frame at the wrong position.
   */
  useLayoutEffect(() => {
    function measure(): void {
      const anchor = anchorRef.current;
      if (!anchor) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const panelHeight = panel?.offsetHeight ?? 0;
      /* The panel's own width once it exists, which with `matchAnchorWidth` is
         already at least the anchor's. Taking the anchor's instead would put an
         end-aligned or near-the-edge menu in the wrong place as soon as its
         content made it wider. */
      const panelWidth = panel?.offsetWidth ?? rect.width;

      const spaceBelow = window.innerHeight - rect.bottom;
      const flipped = spaceBelow < panelHeight + GAP + VIEWPORT_PADDING && rect.top > panelHeight;

      let left = align === 'end' ? rect.right - panelWidth : rect.left;
      left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, window.innerWidth - panelWidth - VIEWPORT_PADDING),
      );

      setPosition({
        top: flipped ? rect.top - panelHeight - GAP : rect.bottom + GAP,
        left,
        width: rect.width,
      });
    }

    measure();

    /* Re-measure rather than re-render on scroll or resize: the anchor may be
       inside a scrolling table, and a fixed panel would otherwise drift. */
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [anchorRef, panel, align, matchAnchorWidth]);

  return createPortal(
    <div
      ref={setPanel}
      id={id}
      role={role}
      aria-label={ariaLabel}
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        ...(matchAnchorWidth && position ? { minWidth: position.width } : {}),
        /* Hidden until measured, so it never flashes at the wrong spot. */
        visibility: position ? 'visible' : 'hidden',
      }}
      className={cn(
        'z-50 rounded-md border border-border bg-surface shadow-[0_4px_16px_rgba(10,15,30,0.10)]',
        /* A short rise into place once measured. No exit animation: the panel
           unmounts on close by design - see Popover - and holding it around
           to fade would mean keeping a stale position on screen. Menus are
           dismissed decisively, so vanishing reads as correct. */
        'transition-[opacity,transform] duration-quick ease-enter',
        position ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
